<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { format, fromUnixTime } from 'date-fns';
import { ptBR, enUS, es, fr, de, it, ja, ko, ru, zhCN } from 'date-fns/locale';

// Mapeamento de locales do i18n para date-fns
const localeMap = {
  pt_BR: ptBR,
  'pt-BR': ptBR,
  pt: ptBR,
  en: enUS,
  es: es,
  fr: fr,
  de: de,
  it: it,
  ja: ja,
  ko: ko,
  ru: ru,
  zh_CN: zhCN,
  'zh-CN': zhCN,
};

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    receivedAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      receivedAtTimeAgo: dynamicTime(this.receivedAtTimestamp),
      timer: null,
    };
  },
  computed: {
    lastActivityTime() {
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    receivedAtTime() {
      return shortTimestamp(this.receivedAtTimeAgo);
    },
    showReceivedAtTime() {
      return (
        this.receivedAtTimestamp && this.receivedAtTime !== this.createdAtTime
      );
    },
    displayTimestamp() {
      if (this.showReceivedAtTime) {
        return `${this.receivedAtTime} • ${this.createdAtTime} • ${this.lastActivityTime}`;
      }
      return `${this.createdAtTime} • ${this.lastActivityTime}`;
    },
    createdAt() {
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')}: ${this.formatDate(
        this.createdAtTimestamp
      )}`;
    },
    receivedAt() {
      if (!this.receivedAtTimestamp) return '';
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.RECEIVED.LATEST')}: ${this.formatDate(
        this.receivedAtTimestamp
      )}`;
    },
    lastActivity() {
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')}: ${this.formatDate(
        this.lastActivityTimestamp
      )}`;
    },
    tooltipText() {
      const lines = [this.lastActivity, this.createdAt];
      if (this.receivedAt) {
        lines.push(this.receivedAt);
      }
      return lines.join('\n');
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
    },
    receivedAtTimestamp() {
      this.receivedAtTimeAgo = dynamicTime(this.receivedAtTimestamp);
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      const locale = localeMap[this.$root.$i18n.locale] || enUS;
      const is24Hour = this.$root.$i18n.locale.startsWith('pt');
      const timeFormat = is24Hour ? 'HH:mm:ss' : 'h:mm:ss a';
      const dateTime = fromUnixTime(timestamp);
      return format(dateTime, `d 'de' MMM 'de' yyyy, ${timeFormat}`, {
        locale,
      });
    },
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.receivedAtTimeAgo = dynamicTime(this.receivedAtTimestamp);
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ displayTimestamp }}</span>
  </div>
</template>
